import { computed, observable } from 'mobx';
import type { ChargingDepartmentShareMappingResponseArgs } from './charging_department_share_mapping2';
import ChargingDepartmentShareMapping, {
  convertChargingDepartmentShareMappingResponseToArgs
} from './charging_department_share_mapping2';
import type Department from './department2';

export type ChargingDepartmentShareResponseArgs = {
  id: number;
  organization_id: number;
  code: string;
  name: string;
  disabled: boolean;
  editable: boolean;
  charging_department_share_mappings: ChargingDepartmentShareMappingResponseArgs[];
};

export type ChargingDepartmentShareArgs = {
  id: number;
  organizationId: number;
  code: string;
  name: string;
  disabled: boolean;
  editable: boolean;
  chargingDepartmentShareMappings: ChargingDepartmentShareMapping[];
};

export const convertChargingDepartmentShareResponseToArgs = (
  response: ChargingDepartmentShareResponseArgs
): ChargingDepartmentShareArgs => {
  return {
    id: response.id,
    organizationId: response.organization_id,
    code: response.code,
    name: response.name,
    disabled: response.disabled,
    editable: response.editable,
    chargingDepartmentShareMappings: response.charging_department_share_mappings.map(
      mapping => new ChargingDepartmentShareMapping(convertChargingDepartmentShareMappingResponseToArgs(mapping))
    )
  };
};

export default class ChargingDepartmentShare {
  @observable id: number;
  @observable organizationId: number;
  @observable code: string;
  @observable name: string;
  @observable disabled: boolean;
  @observable editable: boolean;
  @observable chargingDepartmentShareMappings: ChargingDepartmentShareMapping[];

  constructor(args: ChargingDepartmentShareArgs) {
    this.id = args.id;
    this.organizationId = args.organizationId;
    this.code = args.code;
    this.name = args.name;
    this.disabled = args.disabled;
    this.editable = args.editable;
    this.chargingDepartmentShareMappings = args.chargingDepartmentShareMappings;
  }

  @computed get codeAndNameLines() {
    return [`${this.code}-${this.name}`, this.shareLabel];
  }

  @computed get shareLabel(): string {
    let content = '';
    this.percentages.forEach(item => {
      const department = item.department;
      if (department) {
        if (content) {
          content += ' : ';
        }
        content += `${department.code}-${department.name}(${item.percentage}%)`;
      }
    });

    return content;
  }

  @computed get percentages(): { department: Department; percentage: number }[] {
    const shareMappings = this.chargingDepartmentShareMappings;
    if (shareMappings && shareMappings.length > 0) {
      const totalWeight = shareMappings.reduce((sum, shareMapping) => sum + shareMapping.weight, 0);
      const percentageList = shareMappings.map(shareMapping =>
        Math.floor((100 * shareMapping.weight) / totalWeight)
      );
      const totalPercentage = percentageList.reduce((sum, percentage) => sum + percentage, 0);
      percentageList[0] += 100 - totalPercentage;

      return shareMappings.map((shareMapping, i) => ({
        department: shareMapping.department,
        percentage: percentageList[i]
      }));
    }
    return [];
  }

  @computed get codeAndNameOneLine(): string {
    return `${this.code}:${this.name}`;
  }
}
